import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Calendar, CheckCircle2, XCircle, Clock, ChevronLeft, ChevronRight, FileText } from 'lucide-react';
import Swal from 'sweetalert2';
import UseAxiosSecure from '../hooks/UseAxiosSecure';
import UseAuth from '../hooks/UseAuth';

const LeaveManagement = () => {
    const [page, setPage] = useState(1);
    
    const axiosSecure = UseAxiosSecure();
    const queryClient = useQueryClient();
    const { activeBranchId } = UseAuth();

    // 1. FETCH PAGINATED LEAVES
    const { data: queryData, isLoading } = useQuery({
        queryKey: ['leave-management', activeBranchId, page],
        queryFn: async () => {
            const res = await axiosSecure.get('/hr/leaves/pending', {
                params: { page, limit: 10 }
            });
            return res.data?.data || {};
        },
        enabled: !!activeBranchId,
        keepPreviousData: true
    });

    const leaves = queryData?.leaves || [];
    const totalPages = queryData?.totalPages || 1;
    const totalLeaves = queryData?.totalLeaves || 0;

    // 2. PROCESS LEAVE MUTATION
    const processLeaveMutation = useMutation({
        mutationFn: async ({ attendanceId, status }) => {
            const res = await axiosSecure.put('/hr/leave/process', { attendanceId, status });
            return res.data;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries(['leave-management', activeBranchId]);
            Swal.fire({ icon: 'success', title: 'Processed!', text: data.message, background: '#1a103c', color: '#fff', timer: 1500, showConfirmButton: false });
        },
        onError: (error) => {
            Swal.fire({ icon: 'error', title: 'Error', text: error.response?.data?.message || 'Failed to process leave', background: '#1a103c', color: '#fff' });
        }
    });

    const handleProcessLeave = (attendanceId, status, employeeName) => {
        Swal.fire({
            title: `${status} Leave?`,
            text: `${employeeName} will be notified immediately.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: status === 'Approved' ? '#10b981' : '#ef4444',
            cancelButtonColor: '#64748b',
            confirmButtonText: `Yes, ${status}`,
            background: '#1a103c',
            color: '#fff'
        }).then((result) => {
            if (result.isConfirmed) {
                processLeaveMutation.mutate({ attendanceId, status });
            }
        });
    };

    if (!activeBranchId) return <div className="text-white text-center mt-20">Loading Leave Management...</div>;

    return (
        <div className="max-w-7xl mx-auto space-y-6">
            
            {/* --- HEADER --- */}
            <div className="bg-[#1a103c]/40 backdrop-blur-md p-6 rounded-3xl border border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                        <Calendar className="text-orange-500" /> Leave Management
                    </h2>
                    <p className="text-slate-400 text-sm mt-1">Review and manage staff time-off requests. (Total: {totalLeaves})</p>
                </div>
            </div>

            {/* --- TABLE --- */}
            <div className="bg-[#1a103c]/60 backdrop-blur-xl border border-white/5 rounded-3xl overflow-hidden shadow-2xl relative min-h-[400px]">
                {isLoading && (
                    <div className="absolute inset-0 bg-[#1a103c]/80 z-20 flex items-center justify-center">
                        <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                )}

                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="border-b border-white/10 text-xs uppercase tracking-widest text-slate-400 bg-white/5">
                                <th className="p-6 font-semibold">Employee</th>
                                <th className="p-6 font-semibold">Leave Date</th>
                                <th className="p-6 font-semibold">Type & Reason</th>
                                <th className="p-6 font-semibold text-center">Status</th>
                                <th className="p-6 font-semibold text-right">Actions</th>
                            </tr>
                        </thead>

                        <tbody className="text-sm">
                            {leaves.length === 0 && !isLoading && (
                                <tr>
                                    <td colSpan="5" className="p-10 text-center text-slate-500">
                                        <FileText size={48} className="mx-auto mb-3 opacity-20" />
                                        <p>No leave requests found.</p>
                                    </td>
                                </tr>
                            )}
                            {leaves.map((leave) => (
                                <tr key={leave._id} className="group hover:bg-white/5 transition-colors border-b border-white/5 last:border-none">
                                    <td className="p-6">
                                        <div className="flex items-center gap-3">
                                            <img 
                                                src={leave.employeeId?.userId?.profileImage || `https://ui-avatars.com/api/?name=${leave.employeeId?.userId?.name}&background=random`} 
                                                alt="Profile" 
                                                className="w-10 h-10 rounded-xl object-cover border-2 border-white/10"
                                            />
                                            <h3 className="font-bold text-white">{leave.employeeId?.userId?.name || 'Unknown'}</h3>
                                        </div>
                                    </td>
                                    
                                    <td className="p-6 font-medium text-slate-300">
                                        {new Date(leave.date).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                                    </td>
                                    
                                    <td className="p-6">
                                        <span className="text-xs font-bold text-orange-400 uppercase tracking-wider block mb-1">
                                            {leave.leaveType}
                                        </span>
                                        <span className="text-slate-400 text-xs line-clamp-2 max-w-xs">{leave.notes}</span>
                                    </td>
                                    
                                    <td className="p-6 text-center">
                                        <span className={`px-3 py-1 rounded-full text-xs font-bold border flex w-fit mx-auto items-center gap-1 ${
                                            leave.status === 'Approved' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 
                                            leave.status === 'Rejected' ? 'bg-red-500/10 text-red-400 border-red-500/20' : 
                                            'bg-orange-500/10 text-orange-400 border-orange-500/20'
                                        }`}>
                                            {leave.status === 'Approved' ? <CheckCircle2 size={12}/> : leave.status === 'Rejected' ? <XCircle size={12}/> : <Clock size={12}/>}
                                            {leave.status.toUpperCase()}
                                        </span>
                                    </td>

                                    <td className="p-6 text-right">
                                        {(leave.status === 'Pending' || leave.status === 'On Leave') ? (
                                            <div className="flex justify-end gap-2">
                                                <button 
                                                    onClick={() => handleProcessLeave(leave._id, 'Rejected', leave.employeeId?.userId?.name)} 
                                                    className="p-2 bg-red-500/10 hover:bg-red-500/20 text-red-500 rounded-lg transition-colors tooltip-trigger"
                                                    title="Reject"
                                                >
                                                    <XCircle size={18} />
                                                </button>
                                                <button 
                                                    onClick={() => handleProcessLeave(leave._id, 'Approved', leave.employeeId?.userId?.name)} 
                                                    className="p-2 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 rounded-lg transition-colors tooltip-trigger"
                                                    title="Approve"
                                                >
                                                    <CheckCircle2 size={18} />
                                                </button>
                                            </div>
                                        ) : (
                                            <span className="text-xs text-slate-500 font-bold uppercase">Processed</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {/* --- PAGINATION CONTROLS --- */}
                    <div className="p-4 flex justify-end items-center gap-4 border-t border-white/10 bg-black/10">
                        <span className="text-sm text-slate-400">Page {page} of {totalPages}</span>
                        <div className="flex gap-2">
                            <button 
                                disabled={page === 1} 
                                onClick={() => setPage(p => p - 1)} 
                                className="p-2 bg-white/5 rounded-lg disabled:opacity-30 hover:bg-white/10 text-white transition-colors"
                            >
                                <ChevronLeft size={18}/>
                            </button>
                            <button 
                                disabled={page >= totalPages} 
                                onClick={() => setPage(p => p + 1)} 
                                className="p-2 bg-white/5 rounded-lg disabled:opacity-30 hover:bg-white/10 text-white transition-colors"
                            >
                                <ChevronRight size={18}/>
                            </button>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default LeaveManagement;